import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SECTORS, gramajeRange } from '../data/sectors';

/**
 * Selector de sector en Home.
 *
 * Por qué existe: el hero y el título de la página apuntan al uso de mayor
 * volumen (tortillas), y eso no se toca — es la posición más fuerte del
 * sitio. Este bloque es la puerta lateral: alguien que llega buscando "papel
 * para pizzería" ve su propio negocio nombrado en la página en vez de tener
 * que deducir que el mismo papel le sirve.
 *
 * Composición: la primera tarjeta es DOBLE de ancha y lleva el titular de la
 * sección adentro, en vez de poner un encabezado centrado arriba y cinco
 * tarjetas idénticas debajo. La rejilla se rompe a propósito — es el mismo
 * criterio editorial del bloque de guías, que tampoco es un grid de tarjetas.
 *
 * `products` llega de Home (que ya los tiene cargados para el hero y la
 * rejilla de producto): no se hace una segunda petición. Sirve para el rango
 * de gramaje de cada tarjeta; mientras el catálogo no ha llegado, las
 * tarjetas se ven exactamente como antes y la banda no se renderiza — no
 * hay hueco reservado ni salto de layout, la banda es `absolute`.
 */
export default function SectorPicker({ products = [] }) {
    return (
        <section
            className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-12"
            data-testid="sector-picker"
            aria-labelledby="sector-picker-title"
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Celda de titular: ocupa dos columnas en escritorio y arranca
                    la rejilla desalineada respecto a las tarjetas. */}
                <div className="lg:col-span-2 flex flex-col justify-center py-2 pr-0 lg:pr-8">
                    <p className="eyebrow mb-4">Por tipo de negocio</p>
                    {/* Escala fluida en vez de dos escalones (text-2xl md:text-3xl):
                        el titular crece de forma continua con el viewport en vez de
                        saltar en un breakpoint, y llega a 44px en escritorio —
                        bastante por encima del 3xl que tenía, sin alcanzar el
                        titular del bloque de guías, que sigue siendo el más grande
                        de la página. La jerarquía se respeta, el bloque deja de
                        pedir permiso.
                        El corte de color no es adorno: separa la premisa ("el
                        mismo papel") de la promesa ("resuelve problemas
                        distintos") dentro de una sola frase, usando el acento que
                        ya existe — ningún color nuevo. kraft-deep sobre papel da
                        5.03:1, AA incluso para texto normal. */}
                    <h2
                        id="sector-picker-title"
                        className="font-heading text-[clamp(1.875rem,3.6vw,2.75rem)] font-black leading-[0.98] tracking-tight mb-3 text-balance"
                        style={{ color: 'var(--ink)' }}
                    >
                        El mismo papel{' '}
                        <span style={{ color: 'var(--kraft-deep)' }}>
                            resuelve problemas distintos
                        </span>
                    </h2>
                    <p className="text-sm text-[var(--ink-soft)] leading-relaxed max-w-xl text-pretty">
                        Una pizzería pelea contra el aceite que traspasa el cartón; una repostería,
                        contra el pan que se pega. Cambia el gramaje, no el proveedor.
                    </p>
                </div>

                {SECTORS.map((sector, i) => {
                    const rango = gramajeRange(sector, products);
                    return (
                    <Link
                        key={sector.slug}
                        to={`/papel-para/${sector.slug}`}
                        data-testid={`sector-card-${sector.slug}`}
                        /* La última tarjeta ocupa el ancho que sobra de la
                           rejilla. No es capricho de maquetación: con el
                           titular ocupando dos columnas, cinco tarjetas
                           dejaban dos celdas vacías al final y la fila
                           quedaba coja. Que el hueco lo llene justamente
                           taquerías —el giro de mayor volumen del negocio—
                           cierra la cuadrícula y jerarquiza a la vez.

                           `relative` + `overflow-hidden` son lo que hace posible
                           la banda de gramaje: se posiciona contra la tarjeta y
                           queda recortada fuera del borde inferior mientras está
                           en reposo. El fondo y su transición viven en
                           `.sector-card` (index.css), no en un style inline —
                           inline ganaría sobre el :hover. */
                        className={`reveal-on-scroll group sector-card relative overflow-hidden border border-[var(--line)] rounded-sm p-5 flex flex-col justify-between min-h-[132px] hover:border-[var(--kraft)] ${
                            i === SECTORS.length - 1 ? 'sm:col-span-2 lg:col-span-3' : ''
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <div
                                className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-sm"
                                style={{ backgroundColor: 'var(--ink)' }}
                            >
                                <sector.Icon className="w-4 h-4" style={{ color: 'var(--kraft)' }} />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-[var(--ink)] leading-snug">
                                    {sector.label}
                                </h3>
                                <p className="text-sm text-[var(--ink-soft)] leading-relaxed mt-1">
                                    {sector.hook}
                                </p>
                            </div>
                        </div>
                        {/* mt-6, no mt-4: la banda mide 56px y la fila inferior
                            más el padding solo ocupan 38.5px, así que la banda
                            invade 17.5px POR ENCIMA de la fila. Con 16px de
                            separación el borde de la banda cortaba la segunda
                            línea del gancho ("...pedidos / para llevar") en las
                            tarjetas donde el texto envuelve; con 24px queda
                            holgura y nunca toca el texto. En las tarjetas de
                            gancho corto no cambia nada: manda el
                            `justify-between`, que ya deja más aire que este
                            margen. */}
                        <div className="flex items-center justify-between mt-6">
                            {/* `sector-index` solo se marca cuando HAY gramaje que
                                poner en su lugar: si el catálogo aún no cargó o el
                                sector no tiene producto con gramaje, el número se
                                queda —nunca una fila vacía. */}
                            <span
                                aria-hidden="true"
                                className={`font-mono text-[11px] text-[var(--ink-dim)] ${rango ? 'sector-index' : ''}`}
                            >
                                {String(i + 1).padStart(2, '0')}
                            </span>
                            {rango && (
                                <span className="sector-gramaje-inline font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]">
                                    {rango.label}
                                </span>
                            )}
                            <ArrowRight className="w-4 h-4 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                        </div>

                        {/* La banda: sube desde el borde inferior y sustituye el
                            número de índice —que es decorativo— por el gramaje
                            real del giro. La flecha se repite en la misma
                            coordenada exacta que la de abajo, así que no
                            desaparece ni salta: la superficie cambia debajo de
                            ella. Solo se pinta si el catálogo ya cargó y el
                            sector tiene productos con gramaje. */}
                        {rango && (
                            <div className="gramaje-band" data-testid={`sector-gramaje-${sector.slug}`}>
                                {/* Fila interna con las MISMAS clases que la fila
                                    de reposo de arriba: es lo que garantiza que la
                                    flecha caiga en la misma coordenada exacta. */}
                                <div className="w-full flex items-center justify-between">
                                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]">
                                        {rango.label}
                                    </span>
                                    <ArrowRight aria-hidden="true" className="w-4 h-4 text-[var(--kraft-deep)]" />
                                </div>
                            </div>
                        )}
                    </Link>
                    );
                })}
            </div>
        </section>
    );
}
